import React, { createContext, useContext, useEffect, useState } from "react";

// Create a context
const BasketContext = createContext();

// Provider component
export const BasketProvider = ({ children }) => {
  const [basketQuantity, setBasketQuantity] = useState(0);
  // Lives here rather than in the Header so anything can open the basket —
  // the chat needs to after adding a set, and it has no way to reach the
  // Header's local state.
  const [isBasketOpen, setIsBasketOpen] = useState(false);
  const [basketItems, setBasketItems] = useState([]);
  const [storageReady, setStorageReady] = useState(false);

  useEffect(() => {
    try {
      const localData = localStorage.getItem("basket");
      setBasketItems(localData ? JSON.parse(localData) : []);
    } catch (error) {
      console.error("Error reading basket from localStorage", error);
      setBasketItems([]);
    } finally {
      setStorageReady(true);
    }
  }, []);

  useEffect(() => {
    if (!storageReady) return;

    // Save basket items to localStorage whenever they change
    localStorage.setItem("basket", JSON.stringify(basketItems));
    const count = basketItems.reduce((total, item) => total + item.quantity, 0);
    setBasketQuantity(count);
  }, [basketItems, storageReady]);

  const addToBasket = async (newItem) => {
    setBasketItems((currentItems) => {
      const existingItemIndex = currentItems.findIndex(
        (item) => item.id === newItem.id
      );
      if (existingItemIndex !== -1) {
        // Update quantity of the existing item
        return currentItems.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // Add new item
        return [...currentItems, newItem];
      }
    });
  };

  // Scroll-lock follows the open state rather than being toggled alongside it,
  // so the two cannot drift apart.
  useEffect(() => {
    document.body.classList.toggle("overflow-hidden", isBasketOpen);
    return () => document.body.classList.remove("overflow-hidden");
  }, [isBasketOpen]);

  const openBasket = () => setIsBasketOpen(true);
  const closeBasket = () => setIsBasketOpen(false);
  const toggleBasket = () => setIsBasketOpen((open) => !open);

  const removeItem = (itemId) => {
    setBasketItems((currentItems) =>
      currentItems.filter((item) => item.id !== itemId)
    );
  };

  const emptyBasket = () => {
    setBasketItems([]);
  };

  return (
    <BasketContext.Provider
      value={{
        basketItems,
        addToBasket,
        removeItem,
        basketQuantity,
        emptyBasket,
        isBasketOpen,
        openBasket,
        closeBasket,
        toggleBasket,
      }}
    >
      {children}
    </BasketContext.Provider>
  );
};

export const useBasket = () => useContext(BasketContext);
